/**
 * What the checkout core tells whoever hosts it: the frame → parent half of
 * the drop-in's wire, as types. The web frame posts these to the partner's
 * page inside a versioned envelope (the loader and the frame bridge, in
 * `apps/ticketing-checkout`, own the envelope, the origin checks and the
 * parent → frame half); a native renderer hears the same messages in-process.
 */

export type SucceededPayload = {
  /** The total as the buyer saw it, formatted, e.g. "£24.00". */
  total_label: string;
  /** "Visa 4242", "Apple Pay", "Google Pay", "Visa" — or null when the
   *  component cannot say (a payment that finished in another tab). */
  method_label: string | null;
  buyer_email: string | null;
  /** Host mode: where the status's tick and words ("You're going") are
   *  drawn in the frame's viewport as it ends, so the page's success
   *  screen can take them over in place. */
  status_origin?: StatusOrigin | null;
};

export type StatusRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};
export type StatusOrigin = { mark: StatusRect; words: StatusRect };

/** Frame → parent. `resize` is consumed by the loader and never relayed. */
export type FrameMessage =
  | { type: "ready" }
  | {
      type: "resize";
      height: number;
      /** Animate to it (ms, and a CSS easing) rather than jump: one message
       *  for a whole glide the frame's content makes the same way. */
      duration?: number;
      easing?: string;
    }
  | { type: "session"; expires_at: string }
  | { type: "contact"; email: string }
  | {
      type: "paid";
      order_id: string | null;
      payment_intent_id: string;
      status: string;
    }
  | { type: "paying" }
  | { type: "focus"; top: number; bottom: number }
  | { type: "failed"; message: string; code: string | null }
  | { type: "expired" }
  | { type: "closed" }
  | { type: "change_contact" }
  | { type: "accepted" }
  | ({ type: "succeeded" } & SucceededPayload)
  | TokenRequestMessage;

/**
 * Frame → parent, when the page answers for the buyer (`host_tokens`): a
 * checkout action token for the mounted session, carrying `consents` (the
 * card-reuse consent for a new card kept at pay) or proving the buyer again
 * (`reverify`). The session is the one mounted, so it is not named. Consumed
 * by the loader, which answers with `token_response` under the same `id`;
 * never relayed.
 */
export type TokenRequestMessage = {
  type: "token_request";
  id: string;
  consents: Array<string>;
  reverify: boolean;
};
